import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";
import Header from "../../components/Header";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import TableActions from "../../components/TableActions";
import ArchiveInteractionModal from "../../components/ArchiveInteractionModal";
import { graphqlEndpoint } from "../../config";

import { useTenant } from "../../context/TenantContext";

import { READ_RECENT_INTERACTIONS } from "../../graph/interactions/queries";
import { GET_CONTACT_NAME_LOOKUP } from "../../graph/contacts/queries";
import { ARCHIVE_INTERACTION } from "../../graph/interactions/mutations";
import {
  interactionTypeOptions,
  interactionDirectionOptions,
  valueActionOptions,
} from "../../config/exportFormOptions";

const client = new GraphQLClient(graphqlEndpoint);

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString();
};

const buildLabelMap = (options) =>
  options.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});

const buildContactNameMap = (contacts) =>
  contacts.reduce((acc, contact) => {
    const fullName = `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
    acc[contact.id] = fullName || "Unnamed contact";
    return acc;
  }, {});

const interactionTypeLabelMap = buildLabelMap(interactionTypeOptions);
const interactionDirectionLabelMap = buildLabelMap(interactionDirectionOptions);
const valueActionLabelMap = buildLabelMap(valueActionOptions);

const ManageInteractions = () => {
  const navigate = useNavigate();

  const [interactions, setInteractions] = useState([]);
  const [contactNameMap, setContactNameMap] = useState({});
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState(null);

  const [selectedType, setSelectedType] = useState("");
  const [selectedDirection, setSelectedDirection] = useState("");
  const [selectedValueAction, setSelectedValueAction] = useState("");

  const fetchContacts = useCallback(async () => {
    try {
      const data = await client.request(GET_CONTACT_NAME_LOOKUP);

      setContactNameMap(buildContactNameMap(data.readAllContacts || []));
    } catch (err) {
      console.error("Failed to fetch contact names:", err);
      console.error("GraphQL response errors:", err.response?.errors);
    }
  }, []);

  const fetchInteractions = useCallback(async () => {
    try {
      const data = await client.request(READ_RECENT_INTERACTIONS, {
        limit: 100,
      });

      setInteractions(data.readRecentInteractions || []);
    } catch (err) {
      console.error("Failed to fetch interactions:", err);
      console.error("GraphQL response errors:", err.response?.errors);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
    fetchInteractions();
  }, [fetchContacts, fetchInteractions]);

  const enrichedInteractions = useMemo(() => {
  return interactions.map((interaction) => ({
    ...interaction,
    id: interaction.id,
    contactName: contactNameMap[interaction.contactID] || "Unknown contact",
    typeLabel: interactionTypeLabelMap[interaction.type] || interaction.type || "",
    directionLabel:
      interactionDirectionLabelMap[interaction.direction] ||
      interaction.direction ||
      "",
    valueActionLabel: interaction.valueAction
      ? valueActionLabelMap[interaction.valueAction] || interaction.valueAction
      : "",
  }));
}, [interactions, contactNameMap]);

  const filteredInteractions = useMemo(() => {
    return enrichedInteractions.filter((interaction) => {
      const matchesType = !selectedType || interaction.type === selectedType;
      const matchesDirection =
        !selectedDirection || interaction.direction === selectedDirection;
      const matchesValueAction =
        !selectedValueAction || interaction.valueAction === selectedValueAction;

      return matchesType && matchesDirection && matchesValueAction;
    });
  }, [
    enrichedInteractions,
    selectedType,
    selectedDirection,
    selectedValueAction,
  ]);

  const handleOpenArchiveModal = (interaction) => {
    setSelectedInteraction(interaction);
    setArchiveModalOpen(true);
  };

  const handleCloseArchiveModal = () => {
    setArchiveModalOpen(false);
    setSelectedInteraction(null);
  };

  const handleConfirmArchive = async () => {
    if (!selectedInteraction?.id) {
      return;
    }

    try {
      await client.request(ARCHIVE_INTERACTION, {
        id: selectedInteraction.id,
      });

      setInteractions((prev) =>
        prev.filter((interaction) => interaction.id !== selectedInteraction.id)
      );

      handleCloseArchiveModal();
    } catch (err) {
      console.error("Failed to archive interaction:", err);
      console.error("GraphQL response errors:", err.response?.errors);
    }
  };

  const handleEdit = (id) => {
    navigate(`/interactions/edit/${id}`);
  };

  const columns = [
    { field: "contactName", headerName: "Contact", flex: 1.2 },
    { field: "typeLabel", headerName: "Type", flex: 0.9 },
    { field: "directionLabel", headerName: "Direction", flex: 0.9 },
    { field: "valueActionLabel", headerName: "Value Action", flex: 1.1 },
    { field: "summary", headerName: "Summary", flex: 2 },
    { field: "happenedAt",
      headerName: "Happened At",
      flex: 1.2,
      renderCell: ({ value }) => formatDateTime(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.9,
      sortable: false,
      renderCell: ({ row }) => (
        <TableActions
          onEdit={() => handleEdit(row.id)}
          onDelete={() => handleOpenArchiveModal(row)}
          hideCreate={true}
        />
      ),
    },
  ];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Header
          title="MANAGE INTERACTIONS"
          subtitle="View and manage recent interactions"
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
        <FormControl variant="filled" sx={{ minWidth: 180 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {interactionTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="filled" sx={{ minWidth: 180 }}>
          <InputLabel>Direction</InputLabel>
          <Select
            value={selectedDirection}
            onChange={(e) => setSelectedDirection(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {interactionDirectionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="filled" sx={{ minWidth: 200 }}>
          <InputLabel>Value Action</InputLabel>
          <Select
            value={selectedValueAction}
            onChange={(e) => setSelectedValueAction(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {valueActionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <ThemedDataGrid rows={filteredInteractions} columns={columns} />

      <ArchiveInteractionModal
        open={archiveModalOpen}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
        interaction={selectedInteraction}
      />
    </Box>
  );
};

export default ManageInteractions;